/* eslint-disable react/jsx-boolean-value */
import { memo, Fragment, useState, useEffect, createElement } from 'react';
import { styled, setup, css } from 'goober';
import { shouldForwardProp } from 'goober/should-forward-prop';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { GA4 } from '@happynumbers/front-utils';
import { Tabs } from './Tabs';
import TopicCards from './TopicCards';
import { tabsData, tabsOptions } from '../components/Common/utils';
// import { ArrowDown, ArrowUp } from '../components/Common/Icons';
// import { RandomBanner } from './Banner';
import Select from './Select';
import { BaseContainerStyle, space } from './Shared';

import '../components/WeeklyDashboard/Tooltip.css';
import { gaTrackerIds } from '../../pages/utils';

setup(
  createElement,
  undefined,
  undefined,
  shouldForwardProp((prop) => prop[0] !== '$')
);

const Container = styled(BaseContainerStyle)`
  margin-bottom: 24px;

  @media (min-width: 576px) {
    margin-bottom: 40px;
  }
`;

const Spacer = styled.div`
  ${space}
`;

const Heading = styled.h1`
  font-family: 'Noto Sans', sans-serif;
  font-weight: bold;
  font-size: 22px;
  line-height: 28px;
  letter-spacing: -0.2px;
  color: #242527;

  @media (min-width: 576px) {
    font-size: 36px;
    line-height: 48px;
    letter-spacing: -0.6px;
  }
  ${space}
`;

const Description = styled.p`
  font-family: 'Noto Sans', sans-serif;
  font-size: 13px;
  line-height: 20px;
  color: #242527;
  white-space: pre-line;

  @media (min-width: 576px) {
    font-size: 16px;
    line-height: 24px;
  }
`;

const DescriptionLink = styled.a`
  font-family: 'Noto Sans', sans-serif;
  color: #3097cc;
  text-decoration: none;
  font-size: 13px;
  line-height: 20px;

  @media (min-width: 576px) {
    font-size: 16px;
    line-height: 24px;
  }
`;

const DescriptionPos = styled(Description)`
  margin-bottom: 40px;
  white-space: pre-line;
  text-size-adjust: none;

  @media (max-width: 576px) {
    margin-bottom: 20px;
  }

  @media (max-width: 768px) {
    white-space: normal;
  }

  @media (orientation: portrait) and (max-height: 768px) {
    white-space: normal;
  }

  span {
    display: block;

    @media (min-width: 768px) {
      display: inline;
    }
  }
`;

const GradeTabs = styled(Tabs)`
  display: none;

  @media (min-width: 768px) {
    display: flex;
  }
`;

const GradeSelect = styled(Select)`
  display: inline-block;

  @media (min-width: 768px) {
    display: none;
  }
`;

const ModuleHeading = css`
  font-family: 'Noto Sans', sans-serif;
  font-weight: bold;
  font-size: 18px;
  line-height: 24px;
  color: #242527;
`;

const TopicSummary = styled.summary`
  margin: 0;
  outline: none;
  list-style-image: url('https://static.happynumbers.com/teachers/curriculum/right-arrow.svg');

  &::marker,
  &::-webkit-details-marker {
    background: url('https://static.happynumbers.com/teachers/curriculum/right-arrow.svg');
    color: transparent;
  }
`;

const TopicHeading = styled.h3`
  font-family: 'Noto Sans', sans-serif;
  font-size: 16px;
  line-height: 24px;
  font-weight: normal;
  color: #242527;
  margin: 0;
  text-align: left;
  display: inline;
  text-size-adjust: none;
  padding-left: 14px;
`;

const ModuleHeadingPos = css`
  margin-top: 16px;
  margin-bottom: 8px;

  @media (min-width: 576px) {
    margin-top: 40px;
    margin-bottom: 10px;
  }
`;

const Topic = styled.details`
  border: none;
  background: none;
  cursor: pointer;
  outline: none;
  margin-top: 0;
  padding: 6px 0;
  width: 100%;
  padding-right: 15px;
  padding-right: max(env(safe-area-inset-right), 15px);
  padding-left: 15px;
  padding-left: max(env(safe-area-inset-left), 15px);
  margin-right: auto;
  margin-left: auto;
  box-sizing: border-box;
  max-width: 1024px;

  &:nth-child(odd) {
    background: #f2fcff;

    @media (min-width: 576px) {
      background: #fff;
    }
  }

  @media (min-width: 576px) {
    margin-top: 12px;
    padding-left: 25px;
  }
`;

const TopicDescription = styled.p`
  font-family: 'Noto Sans', sans-serif;
  font-size: 16px;
  line-height: 24px;
  color: #777b8b;
`;

const TopicDescriptionPos = styled(TopicDescription)`
  margin-left: 10px;
  margin-top: 8px;

  @media (min-width: 576px) {
    margin-left: 32px;
    margin-top: 16px;
  }
`;

// const ArrowDownPos = styled(ArrowDown)`
//   flex: 0 0 24px;
//   ${space}
// `;

// const ArrowUpPos = styled(ArrowUp)`
//   flex: 0 0 24px;
//   ${space}
// `;

const AllCards = new Map();

const Curriculum = ({ isPublic }) => {
  const [activeTopicId, setActiveTopicId] = useState();
  const [gradeLessons, setGradeLessons] = useState();
  const [isAuthorized, setIsAuthorized] = useState();

  const { classID, gradeName } = useParams();

  const navigate = useNavigate();

  const {
    title,
    heading: grade,
    description,
  } = tabsData.find((x) => x.link === gradeName);

  useEffect(() => {
    fetch(`/api/auth`)
      .then((data) => data.json())
      .then((x) => {
        setIsAuthorized(x.status);
      });
  }, []);

  useEffect(() => {
    fetch(`/api/curriculum/grade_lessons/${gradeName}`)
      .then((data) => data.json())
      .then((x) => {
        setGradeLessons(x);
      });
    document
      .querySelectorAll('[rel="canonical"]')[0]
      .setAttribute(
        'href',
        `${window.location.protocol}//${window.location.hostname}/math/${gradeName}`
      );
    document
      .querySelectorAll('[property="og:description"]')[0]
      .setAttribute('content', `${description}`);
  }, [gradeName]);

  useEffect(() => {
    const early = window.document.querySelector('.early-content');

    if (early) {
      early.remove();
    }
  }, []);

  const handleSelect = ({ target }) => {
    const { value } = target;

    if (isPublic) {
      navigate(`/math/${value}`);
    } else {
      navigate(`/teachers/g/${classID}/curriculum/${value}`);
    }
  };

  useEffect(() => {
    if (!GA4.isInit) {
      GA4.init(gaTrackerIds);
    }
  }, []);

  return (
    <>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <Container>
        <Heading mb={16} mt={32}>
          Curriculum for {grade}
        </Heading>
        <DescriptionPos>
          Driven by Pedagogy, Supported by Technology (and not vice versa),
          Happy Numbers will make your math centers deliver results.
          <span>
            {!isAuthorized && ` Don’t have an account yet? `}
            {!isAuthorized && (
              <DescriptionLink href='/t/auth/signup'>
                Sign up here.
              </DescriptionLink>
            )}
          </span>
        </DescriptionPos>
        <GradeTabs
          arr={tabsData}
          isPublic={isPublic}
          classID={classID}
          gradeName={gradeName}
        />
        <GradeSelect
          options={tabsOptions}
          value={gradeName}
          onChange={handleSelect}
        />
      </Container>
      {gradeLessons?.topics[0].chapters?.map(({ id, name, lessons }) => (
        <Fragment key={id}>
          <BaseContainerStyle
            as='h2'
            className={`${ModuleHeading} ${ModuleHeadingPos}`}
          >
            {name}
          </BaseContainerStyle>
          {lessons.map((lesson) => (
            <Fragment key={lesson.id}>
              <Topic
                onToggle={() => {
                  if (activeTopicId !== lesson.id) {
                    setActiveTopicId(lesson.id);
                    AllCards.set(lesson.id, lesson.id);
                  } else {
                    setActiveTopicId();
                  }
                }}
              >
                <TopicSummary>
                  {/* { activeTopicId !== lesson.id ? (
                    <ArrowDownPos mr={8} />
                  ) : (
                    <ArrowUpPos mr={8} />
                  ) } */}
                  <TopicHeading>{lesson.name}</TopicHeading>
                </TopicSummary>
                <TopicDescriptionPos>{lesson.description}</TopicDescriptionPos>
                {/* <Suspense fallback={<p>Loading...</p>}>
                  <Topic lessonId={lesson.id} />
                </Suspense> */}
                <TopicCards lessonId={lesson.id} />
              </Topic>
            </Fragment>
          ))}
        </Fragment>
      ))}
      <Spacer mt={isAuthorized ? 88 : 148} />
      {/* { !isAuthorized && (<RandomBanner />) } */}
    </>
  );
};

function areEqual(prevProps, nextProps) {
  return prevProps === nextProps;
}

export default memo(Curriculum, areEqual);
